import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import {
  ArrowPathIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

interface GoogleSheetsReconnectProps {
  providerId: string;
  providerName?: string;
  spreadsheetId?: string;
  sheetName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Google OAuth configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Use a dedicated redirect URI for reconnection to avoid conflicts
const GOOGLE_REDIRECT_URI = window.location.origin + '/provider/settings';
const GOOGLE_AUTH_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly';

/**
 * Component to handle re-authentication for Google Sheets providers
 * when their OAuth tokens have expired or been revoked
 */
const GoogleSheetsReconnect: React.FC<GoogleSheetsReconnectProps> = ({
  providerId,
  providerName,
  spreadsheetId,
  sheetName,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'info' | 'oauth' | 'complete'>('info');

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = () => {
    // Check if we're returning from OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const storedProviderId = sessionStorage.getItem('reconnect_provider_id');

    if (code && state && storedProviderId === providerId) {
      // Store in session storage
      sessionStorage.setItem('google_reauth_code', code);
      sessionStorage.setItem('google_reauth_state', state);
      setStep('oauth');

      // Automatically complete the reconnection
      completeReconnection(code);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const initiateReconnection = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google OAuth is not configured. Please contact your administrator.');
      return;
    }

    // Generate random state for CSRF protection
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('oauth_reauth_state', state);
    sessionStorage.setItem('reconnect_provider_id', providerId);

    // Build OAuth URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', GOOGLE_REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GOOGLE_AUTH_SCOPE);
    authUrl.searchParams.set('access_type', 'offline'); // Get refresh token
    authUrl.searchParams.set('prompt', 'consent'); // Force consent to get fresh refresh token
    authUrl.searchParams.set('state', state);

    // Redirect to Google OAuth
    window.location.href = authUrl.toString();
  };

  const completeReconnection = async (authCode?: string) => {
    setLoading(true);

    try {
      const code = authCode || sessionStorage.getItem('google_reauth_code');

      if (!code) {
        throw new Error('Authorization code not found. Please try again.');
      }

      // Exchange auth code for tokens via backend
      const tokenResponse = await exchangeCodeForTokens(code);

      // Update provider credentials using set_provider_google_sheets_credentials
      const { data: updateData, error: updateError } = await supabase.rpc(
        'set_provider_google_sheets_credentials',
        {
          p_provider_id: providerId,
          p_refresh_token: tokenResponse.refresh_token,
          p_access_token: tokenResponse.access_token,
          p_spreadsheet_id: spreadsheetId || '',
          p_sheet_name: sheetName || 'Sheet1',
        }
      );

      if (updateError) throw updateError;

      // Clear the requires_reauth flag
      const { error: clearError } = await supabase.rpc(
        'clear_provider_reauth_requirement',
        { p_provider_id: providerId }
      );

      if (clearError) {
        console.warn('Failed to clear reauth flag:', clearError);
        // Don't fail the whole operation for this
      }

      // Clean up session storage
      sessionStorage.removeItem('google_reauth_code');
      sessionStorage.removeItem('google_reauth_state');
      sessionStorage.removeItem('oauth_reauth_state');
      sessionStorage.removeItem('reconnect_provider_id');

      toast.success('Google Sheets provider successfully reconnected!');
      setStep('complete');

      // Delay callback to show success message
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (error) {
      console.error('Error completing reconnection:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reconnect Google Sheets');
      setStep('info'); // Go back to info step on error
    } finally {
      setLoading(false);
    }
  };

  const exchangeCodeForTokens = async (code: string): Promise<{ access_token: string; refresh_token: string }> => {
    // Exchange authorization code for tokens via secure backend endpoint
    const { data, error } = await supabase.functions.invoke('exchange-google-oauth-token', {
      body: {
        code,
        redirect_uri: GOOGLE_REDIRECT_URI,
      },
    });

    if (error) {
      console.error('Token exchange error:', error);
      throw new Error(`Failed to exchange authorization code: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data received from token exchange');
    }

    if (data.error === 'no_refresh_token') {
      throw new Error(data.message || 'No refresh token received from Google. Please try again.');
    }

    if (data.error) {
      throw new Error(data.message || `Token exchange failed: ${data.error}`);
    }

    if (!data.access_token || !data.refresh_token) {
      throw new Error('Invalid response from token exchange: missing tokens');
    }

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };
  };

  // Info/confirmation state
  return (
    <div className=" shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start">
          <ShieldExclamationIcon className="h-12 w-12 text-yellow-500 flex-shrink-0" />
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-medium text-white mb-2">Re-authenticate Google Sheets</h3>

            <div className="mb-4 space-y-2">
              <p className="text-sm text-white/50">
                Your Google Sheets authorization has expired or been revoked. To continue syncing
                products from <strong>{providerName || 'this provider'}</strong>, you need to reconnect your Google account.
              </p>

              {spreadsheetId && (
                <p className="text-sm text-white/35">
                  Spreadsheet: <code className="bg-transparent px-1 py-0.5 rounded text-xs">{spreadsheetId}</code>
                </p>
              )}

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl rounded-md p-3 mt-3">
                <p className="text-xs text-white/70">
                  <strong>What happens next:</strong>
                  <br/>
                  1. You'll be redirected to Google to authorize access
                  <br/>
                  2. We'll securely store new credentials
                  <br/>
                  3. Automatic syncing will resume immediately
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={initiateReconnection}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading && <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                {loading ? 'Reconnecting...' : 'Reconnect with Google'}
              </button>
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-white/15 text-sm font-medium rounded-md text-white/70  hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsReconnect;
